/**
 * The contextual panel beside the week (V19, owner 2026-10-08): one shift, or the week's review.
 * Desktop only; a phone keeps ShiftSheet. Everything it does goes through the page's own actions
 * (the card menu, the drawer, the request routes), so the panel adds no way to write.
 */
import type { ApiAbsence, ApiShift, ApiUser } from '@/api/data';
import { T, useT } from '@/i18n';
import type { ActionCard } from '@/poc-chat/actions';
import { type Entry, plannedHours, ruleText } from '@/rules';
import { oslo, osloISO } from '@/rules';
import { Button } from '@/ui';
import { TimeInput } from '@/ui/TimeInput';
import { DN, fmtDs, fullName, num } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { addDays, dow, isoWeek } from '@timefly/core';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import lk from './ClockedLock.module.css';
import { AdviceBox, type OnChoice } from './Hugin';
import type { Advice } from './advice';
import { plannedLunch, suggestLunch } from './lunch';
import { dateOf, timeText } from './model';
import { linesOf } from './proposals';
import { basisText, limitText } from './warningPopoverAdapter';

export type MenuItem = {
  key: string;
  label: ReactNode;
  onSelect: () => void;
  danger?: boolean;
  disabled?: boolean;
};
/** What the panel says after publishing one shift: the publish and the notice are told apart. */
export type Outcome = { id: number; kind: 'done' | 'refused' | 'unknown'; text: string };
/** One item to look over (handoff 2026-10-08): its job group, its severity, and where it leads. */
export interface ReviewItem {
  key: string;
  group: 'staff' | 'rules' | 'attend' | 'absence' | 'avail' | 'requests';
  sev: 'd' | 'w' | 'i';
  who: string;
  text: string;
  /** opens this shift */
  shiftId?: number;
  /** opens this day in Dag */
  day?: string;
  /** a week rule with no single shift: shows this person's week */
  person?: number;
  /** that week rule, for its explanation (retest R07) */
  rule?: Entry;
  /** a day rule on a shift: its code, so Hugin knows whether a person or the length is the fix (#131) */
  code?: string;
  /** the absence a shift stands in, for the holiday left (#131) */
  absence?: ApiAbsence;
  /** a waiting offer or swap, for what approving it does to both weeks (#131) */
  request?: { what: 'offer' | 'swap'; id: number };
}

/** The words for a shift's plan state (GLOSSARY "Forslag, Utkast, Publisert"). */
export const stateWord = (s: ApiShift) => (s.published ? T('Publisert') : T('Utkast'));

const GROUPS: [ReviewItem['group'], string][] = [
  ['staff', 'Bemanning'],
  ['rules', 'Arbeidstid'],
  ['attend', 'Oppmøte'],
  ['absence', 'Fravær og ferie'],
  ['avail', 'Tilgjengelighet'],
  ['requests', 'Bytter og tilbud'],
];
const SEV: Record<ReviewItem['sev'], string> = { d: 'Brudd', w: 'Advarsel', i: 'Info' };

/** The week's review, grouped by job; each row says who, what, and opens where it can be handled. */
export function ReviewPanel({
  items,
  onOpen,
  onDay,
  onPerson,
  onRule,
  onClose,
  advise,
  onChoice,
  scope,
  onAll,
}: {
  items: ReviewItem[];
  /** the person, day or shift a warning popover kept the review to (issue #146) */
  scope?: string | undefined;
  onAll?: () => void;
  /** Hugin's word on an entry (advice.ts adviceFor, #131); none where a person is not the fix */
  advise?: ((x: ReviewItem) => Advice | null) | undefined;
  onChoice?: OnChoice | undefined;
  onOpen: (shiftId: number) => void;
  onDay: (day: string) => void;
  onPerson: (userId: number) => void;
  /** a week rule opens its explanation, which leads on to the records (retest R07) */
  onRule?: (userId: number, rule: Entry) => void;
  onClose: () => void;
}) {
  return (
    <aside className="vpanel" aria-label={T('Å se over')} data-testid="vreview">
      <div className="vphd">
        <div>
          <b>{items.length === 1 ? T('1 sak å se over') : T('{n} saker å se over', { n: items.length })}</b>
          <span>
            {scope ? T('Bare {scope}.', { scope }) : T('Velg en sak for å se den i planen.')}
            {scope && onAll && (
              <>
                {' '}
                <button type="button" className="link" data-testid="vreview-all" onClick={onAll}>
                  {T('Vis alle')}
                </button>
              </>
            )}
          </span>
        </div>
        <button type="button" className="x" aria-label={T('Lukk')} onClick={onClose}>
          <Icon name="close" />
        </button>
      </div>
      {GROUPS.map(([g, title]) => {
        const list = items
          .filter((x) => x.group === g)
          .sort((a, b) => 'dwi'.indexOf(a.sev) - 'dwi'.indexOf(b.sev));
        if (!list.length) return null;
        return (
          <section key={g} className="vgroup" data-testid={`vgroup-${g}`}>
            <h6>
              {T(title)}, {list.length}
            </h6>
            {list.map((x) => {
              const go =
                x.shiftId != null
                  ? () => onOpen(x.shiftId as number)
                  : x.day
                    ? () => onDay(x.day as string)
                    : x.person != null && x.rule && onRule
                      ? () => onRule(x.person as number, x.rule as Entry)
                      : x.person != null
                        ? () => onPerson(x.person as number)
                        : null;
              const body = (
                <>
                  <span key="sev" className={`vsev ${x.sev}`}>
                    {T(SEV[x.sev])}
                  </span>
                  <span key="row" className="vrow">
                    <b>{x.who}</b>
                    <span>{x.text}</span>
                  </span>
                  {go && (
                    <span key="go" className="vgo" aria-hidden="true">
                      <Icon name="chevronRight" />
                    </span>
                  )}
                </>
              );
              const advice = advise && onChoice ? advise(x) : null;
              const row = go ? (
                <button type="button" key={x.key} className="vitem" onClick={go}>
                  {body}
                </button>
              ) : (
                <div key={x.key} className="vitem">
                  {body}
                </div>
              );
              return advice && onChoice ? (
                <div key={x.key}>
                  {row}
                  <AdviceBox advice={advice} absence={x.absence} onChoice={onChoice} />
                </div>
              ) : (
                row
              );
            })}
          </section>
        );
      })}
    </aside>
  );
}

/** One Hugin proposal: what it changes, and the two answers. Using it confirms exactly this action id. */
export function ProposalPanel({
  card,
  outside = 0,
  editable,
  busy,
  outcome,
  onUse,
  onDrop,
  onClose,
}: {
  card: ActionCard;
  /** how many of its entries fall outside what is on screen */
  outside?: number;
  editable: boolean;
  busy: boolean;
  outcome: string;
  onUse: () => void;
  onDrop: () => void;
  onClose: () => void;
}) {
  const lines = linesOf(card);
  return (
    <aside className="vpanel" aria-label={T('Hugins forslag')} data-testid="vproposal">
      <div className="vphd">
        <div>
          <b>{T('Hugins forslag')}</b>
          <span>{T('Forslag, ikke lagret')}</span>
        </div>
        <button type="button" className="x" aria-label={T('Lukk')} onClick={onClose}>
          <Icon name="close" />
        </button>
      </div>
      <ul className="vlines">
        {lines.map((l) => (
          <li key={l}>{l}</li>
        ))}
      </ul>
      {lines.length > 1 && (
        <p className="hint">{T('Forslaget brukes samlet: {n} vakter.', { n: lines.length })}</p>
      )}
      {outside > 0 && (
        <p className="vout unknown" data-testid="voutside">
          {T('{n} av disse er utenfor det du ser nå, andre datoer eller filtrert bort. De lagres også.', {
            n: outside,
          })}
        </p>
      )}
      <p className="hint">
        {card.kind === 'publish'
          ? T('Publiserer vaktene: de ansatte ser dem og kan bli varslet.')
          : T('Lagres som utkast. Ingen ser dem før de publiseres.')}
      </p>
      {outcome && (
        <p aria-live="polite" className="vout unknown" data-testid="voutcome">
          {outcome}
        </p>
      )}
      {editable && (
        <div className="vacts">
          <Button tone="pri" disabled={busy} onClick={onUse}>
            {card.kind === 'publish' ? T('Publiser') : T('Lagre som utkast')}
          </Button>
          <Button disabled={busy} onClick={onDrop}>
            {T('Forkast')}
          </Button>
        </div>
      )}
    </aside>
  );
}

export type LunchValue = { at: string; len: string };
type LunchProblem = { field: 'at' | 'len'; text: string };
/** The stored pause as the fields show it: empty when none is planned. */
export const lunchBase = (s: ApiShift): LunchValue => {
  const p = plannedLunch(s);
  return { at: p?.from ?? '', len: p ? String(p.minutes) : '' };
};
/**
 * What the shift's save sends for the pause: nothing when unchanged, both null when cleared, else the instant and
 * the minutes; a problem when the fields are half filled. A lunch earlier on the clock than the shift's start
 * belongs to the next day (an overnight shift).
 */
export function lunchWrite(
  s: ApiShift,
  v: LunchValue,
): {
  body?: { planned_lunch_start: string | null; planned_lunch_minutes: number | null };
  problem?: LunchProblem;
} {
  const b = lunchBase(s);
  if (v.at === b.at && v.len === b.len) return {};
  if (!v.at && !v.len) return { body: { planned_lunch_start: null, planned_lunch_minutes: null } };
  if (!/^\d{2}:\d{2}$/.test(v.at)) return { problem: { field: 'at', text: T('Skriv når lunsjen starter.') } };
  const n = Number(v.len);
  if (!Number.isInteger(n) || n < 5 || n > 180)
    return { problem: { field: 'len', text: T('Lunsjen må vare fra 5 til 180 minutter.') } };
  const day = dateOf(s);
  const from = oslo(s.start_time)?.time ?? '00:00';
  return {
    body: { planned_lunch_start: osloISO(v.at < from ? addDay(day) : day, v.at), planned_lunch_minutes: n },
  };
}

/**
 * Pause: one line under the shift's times (owner 2026-10-25, as Sling and Quinyx): the start and the minutes,
 * empty when none is planned. ↻ fills in the company's suggestion past 5.5 hours, as Quinyx's «calculate from the
 * rules»; × clears it. Saved with the shift («Lagre endring»), as every competitor does, not on its own. A plan
 * only; pay follows the break rule and the clock, which the label's tooltip says.
 */
export function LunchRow({
  s,
  editable,
  minutes,
  value,
  onChange,
  problem,
  onInvalidChange,
}: {
  s: ApiShift;
  editable: boolean;
  minutes: number;
  value: LunchValue;
  onChange: (v: LunchValue) => void;
  problem?: LunchProblem | null;
  onInvalidChange?: (invalid: boolean) => void;
}) {
  const hint = suggestLunch(s, minutes);
  const sug = hint ? { at: oslo(hint.start)?.time ?? '', len: String(hint.minutes) } : null;
  const why = T('Bare en plan. Lønn følger pauseregelen og stemplingen.');
  const plan = plannedLunch(s);
  if (!editable)
    return plan ? (
      <div className="fld" data-testid="vlunch">
        <span className="lbl" title={why}>
          {T('Pause')}
        </span>
        <span>
          {plan.from}–{plan.to}
        </span>
      </div>
    ) : null;
  const fill = sug ? T('Fyll inn etter pauseregelen: {at}, {n} min', { at: sug.at, n: sug.len }) : '';
  return (
    <div className="fld" data-testid="vlunch">
      {/* «Fyll inn» on the label's line, as «Endre» beside Dato (owner 2026-10-26: the button broke the pattern) */}
      <span className={`lbl ${lk.row}`}>
        <span title={why}>{T('Pause')}</span>
        {sug && !value.at && !value.len && (
          <button
            type="button"
            className={`lnk ${lk.edit}`}
            data-testid="vlunch-fill"
            aria-label={fill}
            title={fill}
            onClick={() => onChange(sug)}
          >
            {T('Fyll inn')}
          </button>
        )}
      </span>
      <span className="vacts vlunch-in">
        <TimeInput
          className="inp"
          aria-label={T('Lunsj fra')}
          aria-invalid={problem?.field === 'at' || undefined}
          value={value.at}
          onValueChange={(at) => onChange({ ...value, at })}
          onInvalidChange={onInvalidChange}
        />
        <input
          className="inp"
          type="number"
          min={5}
          max={180}
          step={5}
          aria-label={T('Minutter')}
          aria-invalid={problem?.field === 'len' || undefined}
          value={value.len}
          onChange={(e) => onChange({ ...value, len: e.target.value })}
        />
        <span className="vlunch-unit">{T('min')}</span>
        {(value.at || value.len) && (
          <button
            type="button"
            className="vlunch-ic"
            aria-label={T('Fjern pausen')}
            title={T('Fjern pausen')}
            onClick={() => onChange({ at: '', len: '' })}
          >
            <Icon name="close" />
          </button>
        )}
      </span>
      {problem && (
        <span role="alert" className="verr" data-testid="vlunch-error">
          {problem.text}
        </span>
      )}
      {!problem && value.len !== '' && Number(value.len) > minutes && (
        // a longer lunch is allowed; the employer decides, the panel only says it (review, long-lunch warning)
        <span className="hint" data-testid="vlunch-long">
          {T('Lengre enn bedriftens pause på {m} min. Det lagres likevel.', { m: minutes })}
        </span>
      )}
    </div>
  );
}
const addDay = (d: string) => {
  const x = new Date(`${d}T12:00:00Z`);
  x.setUTCDate(x.getUTCDate() + 1);
  return x.toISOString().slice(0, 10);
};

const label = (x: ApiShift) => `${x.user ? fullName(x.user) : T('Ledig vakt')} ${timeText(x)}`;

/** The shift's tasks, each movable to another shift that day; the server moves it whole or not at all. */
export function TaskMoves({
  s,
  others,
  onMove,
  busy,
}: {
  s: ApiShift;
  others: ApiShift[];
  onMove: (assignmentId: number, toShiftId: number) => void;
  busy: (id: number) => boolean;
}) {
  const tasks = s.taskAssignments ?? [];
  if (!tasks.length || !others.length) return null;
  const waiting = busy(s.id);
  return (
    <details className="vgroup vdisc" data-testid="vtasks">
      <summary>{T('Flytt aktiviteter til en annen vakt ({n})', { n: tasks.length })}</summary>
      {tasks.map((a) => (
        <label key={a.id} className="vcand">
          <span>
            <b>{a.shiftTask?.name ?? T('Aktivitet')}</b>
            <small>
              {a.status === 'completed'
                ? T('gjort')
                : a.status === 'in-progress'
                  ? T('påbegynt')
                  : T('ikke gjort')}
            </small>
          </span>
          <select
            className="inp"
            aria-label={`${T('Flytt')} ${a.shiftTask?.name ?? ''}`}
            disabled={waiting}
            value=""
            onChange={(e) => e.target.value && onMove(a.id, Number(e.target.value))}
          >
            <option value="">{T('Flytt til …')}</option>
            {others.map((x) => (
              <option key={x.id} value={x.id} disabled={busy(x.id)}>
                {label(x)}
              </option>
            ))}
          </select>
        </label>
      ))}
    </details>
  );
}

/** Andre i tidsrommet (review): who else works while this shift runs, collapsed until asked for. */
export function Overlap({ s, others }: { s: ApiShift; others: ApiShift[] }) {
  const a = Date.parse(s.start_time);
  const b = Date.parse(s.end_time);
  const on = others
    .filter((x) => Date.parse(x.start_time) < b && Date.parse(x.end_time) > a)
    .sort((x, y) => Date.parse(x.start_time) - Date.parse(y.start_time));
  if (!on.length) return null;
  return (
    <details className="vgroup vdisc" data-testid="voverlap">
      <summary>{T('Andre på jobb samtidig ({n})', { n: on.length })}</summary>
      <ul className="vlist">
        {on.map((x) => (
          // the name and the time as two parts, so the time is never split over lines (UI01-8)
          <li key={x.id} className="vpair" data-testid="voverlap-row">
            <span>{x.user ? fullName(x.user) : T('Ledig vakt')}</span>
            <span>{timeText(x)}</span>
          </li>
        ))}
      </ul>
    </details>
  );
}

/**
 * The edge between this shift and one that starts where it ends, or ends where it starts: one
 * time moves both, saved together or not at all. Not offered when either holds a punch.
 */
export function Edges({
  s,
  others,
  onBoundary,
  busy,
}: {
  s: ApiShift;
  others: ApiShift[];
  onBoundary: (earlierId: number, laterId: number, at: string) => void;
  busy: (id: number) => boolean;
}) {
  const pairs = others.flatMap((x) =>
    x.start_time === s.end_time
      ? [{ earlier: s, later: x, other: x }]
      : x.end_time === s.start_time
        ? [{ earlier: x, later: s, other: x }]
        : [],
  );
  const [at, setAt] = useState<Record<number, string>>({});
  if (!pairs.length) return null;
  return (
    <details className="vgroup vdisc" data-testid="vedges">
      <summary>{T('Skiftet mot vakten før eller etter ({n})', { n: pairs.length })}</summary>
      {pairs.map(({ earlier, later, other }) => {
        const now = oslo(earlier.end_time)?.time ?? '';
        const recorded = !!(earlier.punch_in_time || later.punch_in_time);
        const v = at[other.id] ?? now;
        return (
          <div key={other.id} className="vcand">
            <span>
              <b>{label(other)}</b>
              <small>
                {recorded
                  ? T('Stemplet, kan ikke flyttes')
                  : T('{a} slutter og {b} begynner kl. {t}', {
                      a: earlier.user ? fullName(earlier.user) : T('ledig vakt'),
                      b: later.user ? fullName(later.user) : T('ledig vakt'),
                      t: now,
                    })}
              </small>
            </span>
            {!recorded && v && v !== now && (
              // both shifts' new times and lengths, before anything is saved (handoff 2026-10-08)
              <small className="vpreview" data-testid="vedge-preview">
                {(() => {
                  const day = oslo(earlier.end_time)?.date ?? dateOf(earlier);
                  const edge = Date.parse(osloISO(day, v));
                  const h = (a: number, b: number) => Math.round(((b - a) / 36e5) * 10) / 10;
                  const a0 = Date.parse(earlier.start_time);
                  const b1 = Date.parse(later.end_time);
                  return `${oslo(earlier.start_time)?.time}–${v} (${h(a0, edge)} t), ${v}–${oslo(later.end_time)?.time} (${h(edge, b1)} t)`;
                })()}
              </small>
            )}
            {!recorded && (
              <span className="vacts">
                <input
                  className="inp"
                  type="time"
                  aria-label={`${T('Skiftet mot')} ${label(other)}`}
                  value={v}
                  onChange={(e) => setAt({ ...at, [other.id]: e.target.value })}
                />
                <Button
                  small
                  disabled={!v || v === now || busy(earlier.id) || busy(later.id)}
                  onClick={() => {
                    const day = oslo(earlier.end_time)?.date ?? dateOf(earlier);
                    onBoundary(earlier.id, later.id, osloISO(day, v));
                  }}
                >
                  {T('Flytt skiftet')}
                </Button>
              </span>
            )}
          </div>
        );
      })}
    </details>
  );
}

/** "Vis i planen" (V19): what the week shows. Filters never change a shift, and they decide what Publiser covers. */
export function FilterPanel({
  teams,
  team,
  onTeam,
  statuses,
  status,
  onStatus,
  sorts,
  sort,
  onSort,
  search,
  showTasks,
  onTasks,
  showAvail,
  onAvail,
  money,
  showMoney,
  onMoney,
  planning = true,
  project,
  group,
  onReset,
  onClose,
}: {
  /** false in «Uka» (#118): no publishing hint and no availability */
  planning?: boolean;
  /** «Uka»'s project filter when the company uses projects: an id, 'uten', or null for all */
  project?:
    | {
        projects: { id: number; label: string }[];
        value: string | null;
        onChange: (v: string | null) => void;
        word: { one: string; none: string };
      }
    | undefined;
  teams: { id: number; name: string }[];
  team: number | null;
  onTeam: (id: number | null) => void;
  statuses: [string, string][];
  status: string;
  onStatus: (k: string) => void;
  sorts: [string, string][];
  sort: string;
  onSort: (k: string) => void;
  /** the name search, here only on a phone where the toolbar has no room for it (issue #86) */
  search?: { q: string; onQ: (q: string) => void } | undefined;
  showTasks: boolean;
  onTasks: (v: boolean) => void;
  showAvail: boolean;
  onAvail: (v: boolean) => void;
  /** whether this reader may see cost at all */
  money: boolean;
  showMoney: boolean;
  onMoney: (v: boolean) => void;
  /** the rows under department headings (#199), when the company has teams */
  group?: { on: boolean; onChange: (v: boolean) => void } | undefined;
  onReset: () => void;
  onClose: () => void;
}) {
  return (
    <aside className="vpanel" aria-label={T('Vis i planen')} data-testid="vfilter">
      <div className="vphd">
        <div>
          <b>{T('Vis i planen')}</b>
          <span>{T('Filtrene endrer ikke vaktene.')}</span>
        </div>
        <button type="button" className="x" aria-label={T('Lukk')} onClick={onClose}>
          <Icon name="close" />
        </button>
      </div>
      {search && (
        <label className="vfield">
          {T('Finn ansatt')}
          <input
            type="search"
            className="inp"
            aria-label={T('Finn ansatt')}
            value={search.q}
            onChange={(e) => search.onQ(e.target.value)}
          />
        </label>
      )}
      {teams.length > 0 && (
        <label className="vfield">
          {T('Team')}
          <select
            className="inp"
            value={team ?? ''}
            onChange={(e) => onTeam(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">{T('Hele teamet')}</option>
            {teams.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </select>
        </label>
      )}
      {project && (
        <label className="vfield">
          {project.word.one}
          <select
            className="inp"
            data-testid="uka-project"
            value={project.value ?? ''}
            onChange={(e) => project.onChange(e.target.value || null)}
          >
            <option value="">{T('Alle')}</option>
            {project.projects.map((p) => (
              <option key={p.id} value={String(p.id)}>
                {p.label}
              </option>
            ))}
            <option value="uten">{project.word.none}</option>
          </select>
        </label>
      )}
      <label className="vfield">
        {T('Vakter')}
        <select className="inp" value={status} onChange={(e) => onStatus(e.target.value)}>
          <option value="">{T('Alle vakter')}</option>
          {statuses.map(([k, n]) => (
            <option key={k} value={k}>
              {T(n)}
            </option>
          ))}
        </select>
      </label>
      <label className="vfield">
        {T('Sorter medarbeidere')}
        <select className="inp" value={sort} onChange={(e) => onSort(e.target.value)}>
          {sorts.map(([k, n]) => (
            <option key={k} value={k}>
              {T(n)}
            </option>
          ))}
        </select>
      </label>
      {planning && <p className="hint">{T('Publisering fra hovedknappen gjelder de viste utkastene.')}</p>}
      <label className="vcheck">
        <input type="checkbox" checked={showTasks} onChange={(e) => onTasks(e.target.checked)} />
        {T('Vis aktiviteter')}
      </label>
      {group && (
        <label className="vcheck">
          <input type="checkbox" checked={group.on} onChange={(e) => group.onChange(e.target.checked)} />
          {T('Grupper etter avdeling')}
        </label>
      )}
      {planning && (
        <label className="vcheck">
          <input type="checkbox" checked={showAvail} onChange={(e) => onAvail(e.target.checked)} />
          {T('Vis tilgjengelighet')}
        </label>
      )}
      {money && (
        <>
          <label className="vcheck">
            <input type="checkbox" checked={showMoney} onChange={(e) => onMoney(e.target.checked)} />
            {T('Vis budsjett og kostnader')}
          </label>
          <p className="hint">{T('Valgfritt. Beløpene beholdes når visningen er av.')}</p>
        </>
      )}
      <Button small ghost onClick={onReset}>
        <Icon name="refresh" />
        {T('Nullstill filtre')}
      </Button>
    </aside>
  );
}

/** "Bruk endringene": every proposal on the grid, its lines and its scope, before anything is saved. */
export function AllProposalsPanel({
  cards,
  outside,
  editable,
  busy,
  onUse,
  onDrop,
  onClose,
}: {
  cards: ActionCard[];
  outside: Map<string, number>;
  editable: boolean;
  busy: boolean;
  onUse: () => void;
  onDrop: () => void;
  onClose: () => void;
}) {
  const off = [...outside.values()].reduce((a, b) => a + b, 0);
  const publishes = cards.filter((c) => c.kind === 'publish').length;
  return (
    <aside className="vpanel" aria-label={T('Hugins forslag')} data-testid="vproposals">
      <div className="vphd">
        <div>
          <b>{T('{n} forslag fra Hugin', { n: cards.length })}</b>
          <span>{T('Forslag, ikke lagret')}</span>
        </div>
        <button type="button" className="x" aria-label={T('Lukk')} onClick={onClose}>
          <Icon name="close" />
        </button>
      </div>
      {cards.map((c) => (
        <section key={c.action_id} className="vgroup">
          <h6>
            {c.kind === 'create' ? T('Nye vakter') : c.kind === 'move' ? T('Flytt') : T('Publiser')}
            {outside.get(c.action_id)
              ? `, ${T('{n} utenfor visningen', { n: outside.get(c.action_id) ?? 0 })}`
              : ''}
          </h6>
          <ul className="vlines">
            {linesOf(c).map((l) => (
              <li key={l}>{l}</li>
            ))}
          </ul>
        </section>
      ))}
      {off > 0 && (
        <p className="vout unknown">
          {T('{n} endringer er utenfor det du ser nå, andre datoer eller filtrert bort. De brukes også.', {
            n: off,
          })}
        </p>
      )}
      <p className="hint">
        {publishes
          ? T('Nye vakter og flyttinger lagres som utkast; {n} publiseringer publiserer vaktene.', {
              n: publishes,
            })
          : T('Alt lagres som utkast. Ingenting publiseres.')}
      </p>
      {editable && (
        <div className="vacts">
          <Button tone="pri" disabled={busy} onClick={onUse} data-testid="vuse-all">
            {T('Bruk alle {n}', { n: cards.length })}
          </Button>
          <Button disabled={busy} onClick={onDrop}>
            {T('Forkast alle')}
          </Button>
        </div>
      )}
    </aside>
  );
}

/** "Publiser N": the exact shifts, the scope they were picked from, and any rule they break, before writing. */
export function PublishPanel({
  shifts,
  breaches,
  scope,
  busy,
  onPublish,
  onClose,
}: {
  shifts: ApiShift[];
  breaches: Map<number, string[]>;
  scope: string;
  busy: boolean;
  onPublish: () => void;
  onClose: () => void;
}) {
  const sorted = [...shifts].sort((a, b) => a.start_time.localeCompare(b.start_time));
  const broken = sorted.filter((s) => breaches.get(s.id)?.length).length;
  // grouped by person, open shifts last, so the manager sees who is told what (owner 2026-10-25 "better oversikt")
  const groups = new Map<string, { name: string; list: ApiShift[] }>();
  for (const s of sorted) {
    const k = s.user_id == null ? 'open' : String(s.user_id);
    const g = groups.get(k) ?? { name: s.user ? fullName(s.user) : T('Ledige vakter'), list: [] };
    g.list.push(s);
    groups.set(k, g);
  }
  const ordered = [...groups.entries()].sort(([a], [b]) => (a === 'open' ? 1 : b === 'open' ? -1 : 0));
  const people = ordered.filter(([k]) => k !== 'open').length;
  const hours = sorted.reduce((n, s) => n + plannedHours(s), 0);
  return (
    <aside className="vpanel" aria-label={T('Publiser')} data-testid="vpublish">
      <div className="vphd">
        <div>
          <b>{T('Publiser {n} vakter', { n: sorted.length })}</b>
          <span>
            {T('Utvalg')}: {scope}
          </span>
        </div>
        <button type="button" className="x" aria-label={T('Lukk')} onClick={onClose}>
          <Icon name="close" />
        </button>
      </div>
      <dl className="vpubsum">
        <div>
          <dt>{T('Vakter')}</dt>
          <dd>{sorted.length}</dd>
        </div>
        <div>
          <dt>{T('Ansatte')}</dt>
          <dd>{people}</dd>
        </div>
        <div>
          <dt>{T('Timer')}</dt>
          <dd>{num(hours, 1)}</dd>
        </div>
      </dl>
      <div className="vpubgroups">
        {ordered.map(([k, g]) => (
          <section key={k} className="vpubg">
            <h4>
              <span>{g.name}</span>
              <small>
                {g.list.length === 1 ? T('1 vakt') : T('{n} vakter', { n: g.list.length })},{' '}
                {num(
                  g.list.reduce((n, s) => n + plannedHours(s), 0),
                  1,
                )}{' '}
                t
              </small>
            </h4>
            <ul className="vpublist">
              {g.list.map((s) => (
                <li key={s.id} className={breaches.get(s.id)?.length ? 'd' : ''}>
                  <span className="vpubday">
                    {DN[dow(dateOf(s))]} {fmtDs(dateOf(s))}
                  </span>
                  <span className="vpubtime">{timeText(s)}</span>
                  {breaches.get(s.id)?.length ? (
                    <span className="vpubbad">{breaches.get(s.id)?.join(', ')}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
      <p className="hint">{T('De ansatte ser vaktene og kan bli varslet.')}</p>
      {broken > 0 && (
        <p className="vout unknown">
          {T('{n} av utkastene bryter en regel. Publisering stoppes ikke.', { n: broken })}
        </p>
      )}
      <div className="vacts">
        <Button tone="pub" disabled={busy || !sorted.length} onClick={onPublish} data-testid="vpublish-go">
          <Icon name="upload" />
          {T('Publiser {n} vakter', { n: sorted.length })}
        </Button>
        <Button onClick={onClose}>{T('Avbryt')}</Button>
      </div>
    </aside>
  );
}

/**
 * A week rule explained (retest R07): which rule and its limit, which week, whether the plan or
 * the punches broke it, the shifts that make up that week, and where to go next. The server
 * judges the week from the company's rules; this panel only shows what it said and the records.
 */
export function RulePanel({
  user,
  rule,
  shifts,
  shown,
  onShift,
  onWeek,
  onOnly,
  onClose,
}: {
  user: ApiUser;
  rule: Entry;
  /** this person's shifts in the rule's week, as far as the plan has read them */
  shifts: ApiShift[];
  /** the Monday on screen */
  shown: string;
  onShift: (s: ApiShift) => void;
  onWeek: (monday: string) => void;
  onOnly: () => void;
  onClose: () => void;
}) {
  const t = useT();
  const head = useRef<HTMLElement | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: another rule is a new heading to move to
  useEffect(() => {
    head.current?.focus({ preventScroll: true });
  }, [rule.code, rule.week, user.id]);
  const week = rule.week ?? shown;
  const titleKey = `timeline.group_${rule.code}`;
  const title = t(titleKey) === titleKey ? ruleText(t, rule) : t(titleKey);
  const max = Number(rule.args?.max ?? 0);
  const sorted = [...shifts].sort((a, b) => Date.parse(a.start_time) - Date.parse(b.start_time));
  const planned = sorted.reduce((n, s) => n + plannedHours(s), 0);
  // the punches, where there are any: a rule on punched time is explained by them
  const punchedH = (s: ApiShift) =>
    s.punch_in_time && s.punch_out_time
      ? Math.max(0, (Date.parse(s.punch_out_time) - Date.parse(s.punch_in_time)) / 36e5)
      : null;
  const punched = sorted.some((s) => punchedH(s) != null)
    ? sorted.reduce((n, s) => n + (punchedH(s) ?? 0), 0)
    : null;
  const basis = basisText(rule) ?? T('Planlagt tid etter pause');
  const limit = limitText(rule);
  // what the server counted for the week, where the rule says it: over the limit by `hours`
  const counted = rule.code === 'weekly_over' && max > 0 ? Number(rule.args?.hours ?? 0) + max : null;
  return (
    <aside className="vpanel" aria-labelledby="vrule-t" data-testid="vrule">
      <div className="vphd">
        <div>
          <b id="vrule-t" ref={head} tabIndex={-1}>
            {title}
          </b>
          <span>
            {fullName(user)}, {T('uke {n}', { n: isoWeek(week) })}
          </span>
        </div>
        <button type="button" className="x" aria-label={T('Lukk')} onClick={onClose}>
          <Icon name="close" />
        </button>
      </div>
      <p className={`al ${rule.sev === 'd' ? 'dgr' : 'warn'}`}>{ruleText(t, rule)}</p>
      <dl className="vfacts" data-testid="vrule-facts">
        <div>
          <dt>{T('Periode')}</dt>
          <dd>
            {fmtDs(week)}–{fmtDs(addDays(week, 6))}
          </dd>
        </div>
        {limit && (
          <div>
            <dt>{T('Grense')}</dt>
            <dd>{limit}</dd>
          </div>
        )}
        <div>
          <dt>{T('Regnet på')}</dt>
          <dd>{basis}</dd>
        </div>
        {counted != null && (
          <div>
            <dt>{T('Uka slik den er regnet')}</dt>
            <dd>{num(Math.round(counted * 10) / 10)} t</dd>
          </div>
        )}
        <div>
          <dt>{T('Planlagt i uka')}</dt>
          <dd>{num(Math.round(planned * 10) / 10)} t</dd>
        </div>
        {punched != null && (
          <div>
            <dt>{T('Stemplet i uka')}</dt>
            <dd>{num(Math.round(punched * 10) / 10)} t</dd>
          </div>
        )}
      </dl>
      <p className="hint">
        {T(
          'Serveren regner uka fra mandag til søndag etter bedriftens regler. Varselet stopper ikke publisering.',
        )}
      </p>
      <div className="vgroup" data-testid="vrule-shifts">
        <h4>{T('Vaktene i uka')}</h4>
        {sorted.length ? (
          sorted.map((x) => (
            <button type="button" key={x.id} className="vcand vpick" onClick={() => onShift(x)}>
              <span>
                <b>
                  {DN[dow(dateOf(x))]} {fmtDs(dateOf(x))}, {timeText(x)}
                </b>
                <small>
                  {num(Math.round(plannedHours(x) * 10) / 10)} t {T('planlagt')}
                  {punchedH(x) != null
                    ? `, ${T('stemplet {from}–{to}', { from: oslo(x.punch_in_time)?.time ?? '', to: oslo(x.punch_out_time)?.time ?? '' })}`
                    : x.punch_in_time
                      ? `, ${T('stemplet inn')}`
                      : ''}{' '}
                  , {stateWord(x)}
                </small>
              </span>
              <Icon name="chevronRight" />
            </button>
          ))
        ) : (
          <p className="hint">{T('Ingen av vaktene i den uka er hentet inn her.')}</p>
        )}
      </div>
      <div className="vacts">
        {week !== shown && (
          <Button tone="pri" onClick={() => onWeek(week)}>
            {T('Gå til uke {n}', { n: isoWeek(week) })}
          </Button>
        )}
        <Button onClick={onOnly}>{T('Vis bare {name}', { name: user.name })}</Button>
      </div>
    </aside>
  );
}
